/**
 * Solicitar Proposta — Notes TXT import (structured template).
 * Line shape: `Campo - valor # comentário`
 * Services: numbered by type (`Luz 1`, `Luz 2`, …).
 */

import {
  MOBILIDADE_FIELD_KEYS,
  MOBILIDADE_FIELD_LABELS,
  MOBILIDADE_OPTION_LABELS,
  type MobilidadeFieldKey,
} from './mobilidadeProposalFields';

export const PROPOSAL_NOTES_TEMPLATE = `# MODELO — Solicitar Proposta (Notas)
# Preencha o valor entre " - " e "#". O texto após # é só ajuda e é ignorado.
# Ficheiros (Fatura / Print do Telhado) anexam-se depois no CRM — não vão neste TXT.
# Blocos de serviço vazios (sem campos preenchidos) são ignorados na importação.

Proposta de Marcação -  # Sim/Não
Assinatura -  # Impressa/Digital
Tipo de Pedido -  # normal/roadtrip

Nome da Empresa -
NIF -  # exatamente 9 dígitos
Morada de Correspondência -  # opcional
Localidade -
CAE -  # exatamente 5 dígitos
Nome do Representante -
Contacto do Representante -
Email do Representante -
Email da Empresa -  # opcional

Luz 1 -
Morada de Fornecimento -
CPE -  # PT + 16 dígitos + 2 letras
Potência -  # btn/bte/mt/outra
Potência (outra) -  # só se Potência = outra
Sistema -  # trifasico/monofasico
Data prevista para o Fecho -  # AAAA-MM-DD se roadtrip
Observações -

Luz 2 -
Morada de Fornecimento -
CPE -  # PT + 16 dígitos + 2 letras
Potência -  # btn/bte/mt/outra
Potência (outra) -  # só se Potência = outra
Sistema -  # trifasico/monofasico
Data prevista para o Fecho -  # AAAA-MM-DD se roadtrip
Observações -

Bairro Solar 1 -
Morada de Fornecimento -
CPE -  # PT + 16 dígitos + 2 letras
Potência -  # btn/bte/mt/outra
Potência (outra) -  # só se Potência = outra
Coordenadas Geográficas -  # obrigatório BS
Data prevista para o Fecho -  # AAAA-MM-DD se roadtrip
Observações -

PAP 1 -
Morada de Fornecimento -
CPE -  # PT + 16 dígitos + 2 letras
Potência -  # btn/bte/mt/outra
Potência (outra) -  # só se Potência = outra
Data prevista para o Fecho -  # AAAA-MM-DD se roadtrip
Observações -

Transacional 1 -
Morada de Fornecimento -
CPE -  # PT + 16 dígitos + 2 letras
Potência -  # btn/bte/mt/outra
Potência (outra) -  # só se Potência = outra
Forma de Pagamento -  # pronto/prestacoes
Data prevista para o Fecho -  # AAAA-MM-DD se roadtrip
Observações -

Gás 1 -
Morada de Fornecimento -
CUI -  # deve começar por PT
Escalão -  # 1/2/3/4
Data prevista para o Fecho -  # AAAA-MM-DD se roadtrip
Observações -

Mobilidade 1 -
Morada de Fornecimento -
Plataforma de Gestão -  # Sem gestão / EDP Charge
Local de Instalação -  # Interior/Exterior
Quadro Mobilidade -  # Sim/Não
Tipo de Fixação -  # Solo/Parede
Tipo de Carregamento -  # Normal/Rápido
Distância QVE Próximo -
Distância QVE Distante -
Observações -

Outro 1 -
Morada de Fornecimento -
Observações -  # obrigatório para Outro
`;

const MAX_SERVICE_INSTANCES = 20;

const SERVICE_DISPLAY_TO_KEY: Record<string, string> = {
  'bairro solar': 'bairro-solar',
  'bairro-solar': 'bairro-solar',
  pap: 'pap',
  transacional: 'transacional',
  luz: 'luz',
  gás: 'gas',
  gas: 'gas',
  mobilidade: 'mobilidade',
  outro: 'outro',
};

const SERVICE_HEADER_NAMES = Object.keys(SERVICE_DISPLAY_TO_KEY)
  .sort((a, b) => b.length - a.length);

const EMPTY_QUANTITIES = (): Record<string, number> => ({
  'bairro-solar': 0,
  pap: 0,
  transacional: 0,
  luz: 0,
  gas: 0,
  mobilidade: 0,
  outro: 0,
});

/** Fields allowed per service type (wrong fields → warning + ignore). */
const SERVICE_ALLOWED_FIELDS: Record<string, Set<string>> = {
  'bairro-solar': new Set([
    'moradaFornecimento',
    'cpe',
    'potencia',
    'potenciaOutra',
    'coordenadasGeograficas',
    'dataFecho',
    'observacoes',
  ]),
  pap: new Set([
    'moradaFornecimento',
    'cpe',
    'potencia',
    'potenciaOutra',
    'dataFecho',
    'observacoes',
  ]),
  transacional: new Set([
    'moradaFornecimento',
    'cpe',
    'potencia',
    'potenciaOutra',
    'formaPagamento',
    'dataFecho',
    'observacoes',
  ]),
  luz: new Set([
    'moradaFornecimento',
    'cpe',
    'potencia',
    'potenciaOutra',
    'sistema',
    'dataFecho',
    'observacoes',
  ]),
  gas: new Set([
    'moradaFornecimento',
    'cui',
    'escalao',
    'dataFecho',
    'observacoes',
  ]),
  mobilidade: new Set([
    'moradaFornecimento',
    'observacoes',
    ...MOBILIDADE_FIELD_KEYS,
  ]),
  outro: new Set(['moradaFornecimento', 'observacoes']),
};

type EquipaInternaImport = {
  propostaMarcacao: string;
  assinaturaContrato: string;
  tipoPedido: string;
};

type EmpresaImport = {
  nomeEmpresa: string;
  nifEmpresa: string;
  moradaCorrespondencia: string;
  regiao: string;
  cae: string;
  nomeRepresentante: string;
  contactoRepresentante: string;
  emailRepresentante: string;
  emailEmpresa: string;
};

type ServiceInstanceImport = {
  service: string;
  instanceNumber: number;
  fields: Record<string, string>;
};

export type PartialProposalImport = {
  equipaInterna: EquipaInternaImport;
  empresa: EmpresaImport;
  services: ServiceInstanceImport[];
};

export type ParseProposalNotesResult = {
  ok: boolean;
  data: PartialProposalImport;
  errors: string[];
  warnings: string[];
};

export type ProposalFormState = {
  equipaInterna: Record<string, any>;
  empresa: Record<string, any>;
  servico: Record<string, any>;
  resumo: Record<string, any>;
  servicos: Array<{ service: string; instanceNumber: number; instanceId: string }>;
  serviceQuantities: Record<string, number>;
  serviceDetails: Record<string, any>;
};

const emptyEquipa = (): EquipaInternaImport => ({
  propostaMarcacao: '',
  assinaturaContrato: '',
  tipoPedido: '',
});

const emptyEmpresa = (): EmpresaImport => ({
  nomeEmpresa: '',
  nifEmpresa: '',
  moradaCorrespondencia: '',
  regiao: '',
  cae: '',
  nomeRepresentante: '',
  contactoRepresentante: '',
  emailRepresentante: '',
  emailEmpresa: '',
});

function normalizeLabel(label: string): string {
  return label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function stripInlineComment(line: string): string {
  const hashIdx = line.indexOf('#');
  if (hashIdx === -1) return line;
  return line.slice(0, hashIdx);
}

/** Split `Label - value` (first ` - ` / trailing dash). Allows empty values. */
function splitLabelValue(lineWithoutComment: string): { label: string; value: string } | null {
  const match = lineWithoutComment.match(/^(.+?)\s+-\s*(.*)$/);
  if (!match) return null;
  return {
    label: match[1].trim(),
    value: (match[2] || '').trim(),
  };
}

function matchServiceHeader(label: string): { service: string; instanceNumber: number } | null {
  const normalized = normalizeLabel(label);
  for (const name of SERVICE_HEADER_NAMES) {
    if (normalized === name) {
      // Bare "Luz" without number — treat as instance 1 only if we want; plan requires "Luz 1"
      return null;
    }
    if (normalized.startsWith(name + ' ')) {
      const rest = normalized.slice(name.length).trim();
      const num = parseInt(rest, 10);
      if (Number.isFinite(num) && num >= 1 && String(num) === rest) {
        return { service: SERVICE_DISPLAY_TO_KEY[name], instanceNumber: num };
      }
    }
  }
  return null;
}

type FieldTarget =
  | { kind: 'equipa'; key: keyof EquipaInternaImport }
  | { kind: 'empresa'; key: keyof EmpresaImport }
  | { kind: 'service'; key: string };

function resolveGlobalField(label: string): FieldTarget | null {
  const n = normalizeLabel(label);
  const equipaMap: Record<string, keyof EquipaInternaImport> = {
    'proposta de marcacao': 'propostaMarcacao',
    'proposta de marcacao?': 'propostaMarcacao',
    assinatura: 'assinaturaContrato',
    'assinatura do contrato': 'assinaturaContrato',
    'assinatura do contrato?': 'assinaturaContrato',
    'tipo de pedido': 'tipoPedido',
  };
  if (equipaMap[n]) return { kind: 'equipa', key: equipaMap[n] };

  const empresaMap: Record<string, keyof EmpresaImport> = {
    'nome da empresa': 'nomeEmpresa',
    nif: 'nifEmpresa',
    'morada de correspondencia': 'moradaCorrespondencia',
    localidade: 'regiao',
    regiao: 'regiao',
    cae: 'cae',
    'nome do representante': 'nomeRepresentante',
    'contacto do representante': 'contactoRepresentante',
    'email do representante': 'emailRepresentante',
    'email da empresa': 'emailEmpresa',
  };
  if (empresaMap[n]) return { kind: 'empresa', key: empresaMap[n] };

  return null;
}

function resolveServiceField(label: string): string | null {
  const n = normalizeLabel(label);
  const map: Record<string, string> = {
    'morada de fornecimento': 'moradaFornecimento',
    cpe: 'cpe',
    cui: 'cui',
    potencia: 'potencia',
    'potencia (outra)': 'potenciaOutra',
    sistema: 'sistema',
    escalao: 'escalao',
    'forma de pagamento': 'formaPagamento',
    'coordenadas geograficas': 'coordenadasGeograficas',
    'data prevista para o fecho': 'dataFecho',
    observacoes: 'observacoes',
    'plataforma de gestao': 'plataformaGestao',
    'local de instalacao': 'localInstalacao',
    'quadro mobilidade': 'quadroMobilidade',
    'tipo de fixacao': 'tipoFixacao',
    'tipo de carregamento': 'tipoCarregamento',
    'distancia qve proximo': 'distanciaQveProximo',
    'distancia qve distante': 'distanciaQveDistante',
  };
  // Also match shortened mobilidade questionnaire labels from MOBILIDADE_FIELD_LABELS
  for (const key of MOBILIDADE_FIELD_KEYS) {
    const lab = normalizeLabel(MOBILIDADE_FIELD_LABELS[key]);
    if (lab === n) return key;
  }
  return map[n] || null;
}

function mapEnum(
  raw: string,
  aliases: Record<string, string>,
  fieldLabel: string,
  warnings: string[]
): string {
  if (!raw) return '';
  const key = normalizeLabel(raw);
  if (aliases[key] != null) return aliases[key];
  // Already canonical?
  const values = new Set(Object.values(aliases));
  if (values.has(raw.trim().toLowerCase())) return raw.trim().toLowerCase();
  const allowed = Array.from(new Set(Object.values(aliases))).join(', ');
  warnings.push(`${fieldLabel}: valor "${raw}" inválido (opções: ${allowed}). Campo ignorado.`);
  return '';
}

function validateNif(raw: string, warnings: string[]): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 9) {
    warnings.push(`NIF inválido: esperado exatamente 9 dígitos (recebido "${raw}"). Campo ignorado.`);
    return '';
  }
  return digits;
}

function validateCae(raw: string, warnings: string[]): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 5) {
    warnings.push(`CAE inválido: esperado exatamente 5 dígitos (recebido "${raw}"). Campo ignorado.`);
    return '';
  }
  return digits;
}

function validateEmail(raw: string, warnings: string[], label: string): string {
  if (!raw) return '';
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
  if (!ok) {
    warnings.push(`${label}: email inválido ("${raw}"). Campo ignorado.`);
    return '';
  }
  return raw.trim();
}

function validateContacto(raw: string, warnings: string[]): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length > 0 && (digits.length < 9 || digits.length > 15)) {
    warnings.push(
      `Contacto do Representante: comprimento invulgar (${digits.length} dígitos). Valor mantido — confirme.`
    );
  }
  return raw.trim();
}

function validateCpe(raw: string, warnings: string[]): string {
  if (!raw) return '';
  const cpe = raw.replace(/\s+/g, '').toUpperCase();
  if (!cpe.startsWith('PT') || cpe.length !== 20 || !/^PT\d{16}[A-Z]{2}$/.test(cpe)) {
    warnings.push(
      `CPE inválido: esperado PT + 16 dígitos + 2 letras (20 caracteres). Recebido "${raw}". Campo ignorado.`
    );
    return '';
  }
  return cpe;
}

function validateCui(raw: string, warnings: string[]): string {
  if (!raw) return '';
  const cui = raw.replace(/\s+/g, '').toUpperCase();
  if (!cui.startsWith('PT')) {
    warnings.push(`CUI inválido: deve começar por PT (recebido "${raw}"). Campo ignorado.`);
    return '';
  }
  return cui;
}

function validateDate(raw: string, warnings: string[]): string {
  if (!raw) return '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) {
    warnings.push(`Data prevista para o Fecho inválida: use AAAA-MM-DD (recebido "${raw}"). Campo ignorado.`);
    return '';
  }
  return raw.trim();
}

function mapMobilidadeEnum(
  field: MobilidadeFieldKey,
  raw: string,
  warnings: string[]
): string {
  if (!raw) return '';
  const options = MOBILIDADE_OPTION_LABELS[field];
  if (!options) return raw.trim(); // free-text distance fields

  const n = normalizeLabel(raw);
  for (const [canon, label] of Object.entries(options)) {
    if (normalizeLabel(canon) === n || normalizeLabel(label) === n) return canon;
    // Short aliases
    if (field === 'tipoCarregamento') {
      if (n === 'normal' || n.startsWith('normal')) return 'normal';
      if (n === 'rapido' || n.startsWith('rapido')) return 'rapido';
    }
  }
  const allowed = Object.values(options).join(' / ');
  warnings.push(
    `${MOBILIDADE_FIELD_LABELS[field]}: valor "${raw}" inválido (opções: ${allowed}). Campo ignorado.`
  );
  return '';
}

function sanitizeServiceField(
  service: string,
  key: string,
  raw: string,
  warnings: string[]
): string {
  if (!raw) return '';
  const allowed = SERVICE_ALLOWED_FIELDS[service];
  if (allowed && !allowed.has(key)) {
    warnings.push(
      `Campo "${key}" ignorado para serviço ${service} (não aplicável).`
    );
    return '';
  }

  switch (key) {
    case 'cpe':
      return validateCpe(raw, warnings);
    case 'cui':
      return validateCui(raw, warnings);
    case 'dataFecho':
      return validateDate(raw, warnings);
    case 'potencia':
      return mapEnum(
        raw,
        { btn: 'btn', bte: 'bte', mt: 'mt', outra: 'outra' },
        'Potência',
        warnings
      );
    case 'sistema':
      return mapEnum(
        raw,
        {
          trifasico: 'trifasico',
          monofasico: 'monofasico',
          trifásico: 'trifasico',
          monofásico: 'monofasico',
        },
        'Sistema',
        warnings
      );
    case 'escalao':
      return mapEnum(
        raw,
        { '1': '1', '2': '2', '3': '3', '4': '4' },
        'Escalão',
        warnings
      );
    case 'formaPagamento':
      return mapEnum(
        raw,
        {
          pronto: 'pronto',
          prestacoes: 'prestacoes',
          prestações: 'prestacoes',
        },
        'Forma de Pagamento',
        warnings
      );
    case 'plataformaGestao':
    case 'localInstalacao':
    case 'quadroMobilidade':
    case 'tipoFixacao':
    case 'tipoCarregamento':
      return mapMobilidadeEnum(key, raw, warnings);
    default:
      return raw.trim();
  }
}

function serviceBlockHasContent(fields: Record<string, string>): boolean {
  return Object.values(fields).some((v) => String(v || '').trim() !== '');
}

/**
 * Parse Notes TXT into structured import data (with validation warnings).
 */
export function parseProposalNotesTxt(text: string): ParseProposalNotesResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const equipaInterna = emptyEquipa();
  const empresa = emptyEmpresa();
  const services: ServiceInstanceImport[] = [];

  if (!text || !String(text).trim()) {
    return {
      ok: false,
      data: { equipaInterna, empresa, services },
      errors: ['Ficheiro / texto vazio.'],
      warnings: [],
    };
  }

  let currentService: ServiceInstanceImport | null = null;
  let recognized = 0;

  const lines = String(text).replace(/^\uFEFF/, '').split(/\r?\n/);

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('#')) continue;

    const withoutComment = stripInlineComment(rawLine);
    if (!withoutComment.trim()) continue;

    const parts = splitLabelValue(withoutComment.trim());
    if (!parts) {
      warnings.push(`Linha ignorada (formato esperado "Campo - valor"): ${trimmed.slice(0, 60)}`);
      continue;
    }

    const { label, value } = parts;
    const header = matchServiceHeader(label);
    if (header) {
      if (currentService && serviceBlockHasContent(currentService.fields)) {
        services.push(currentService);
      } else if (currentService) {
        // unused stub — skip silently
      }
      currentService = {
        service: header.service,
        instanceNumber: header.instanceNumber,
        fields: {},
      };
      recognized += 1;
      continue;
    }

    if (currentService) {
      const fieldKey = resolveServiceField(label);
      if (!fieldKey) {
        warnings.push(
          `${currentService.service} ${currentService.instanceNumber}: campo desconhecido "${label}".`
        );
        continue;
      }
      recognized += 1;
      const sanitized = sanitizeServiceField(
        currentService.service,
        fieldKey,
        value,
        warnings
      );
      currentService.fields[fieldKey] = sanitized;
      continue;
    }

    // Global fields (equipa / empresa)
    const target = resolveGlobalField(label);
    if (!target) {
      warnings.push(`Campo desconhecido (fora de serviço): "${label}".`);
      continue;
    }
    recognized += 1;

    if (target.kind === 'equipa') {
      let v = value;
      if (target.key === 'propostaMarcacao') {
        v = mapEnum(
          value,
          { sim: 'sim', nao: 'nao', 'não': 'nao', yes: 'sim', no: 'nao' },
          'Proposta de Marcação',
          warnings
        );
      } else if (target.key === 'assinaturaContrato') {
        v = mapEnum(
          value,
          {
            digital: 'digital',
            impressa: 'impressa',
            impresso: 'impressa',
          },
          'Assinatura',
          warnings
        );
      } else if (target.key === 'tipoPedido') {
        v = mapEnum(
          value,
          { normal: 'normal', roadtrip: 'roadtrip' },
          'Tipo de Pedido',
          warnings
        );
      }
      equipaInterna[target.key] = v;
    } else if (target.kind === 'empresa') {
      let v = value;
      if (target.key === 'nifEmpresa') v = validateNif(value, warnings);
      else if (target.key === 'cae') v = validateCae(value, warnings);
      else if (target.key === 'emailRepresentante') {
        v = validateEmail(value, warnings, 'Email do Representante');
      } else if (target.key === 'emailEmpresa') {
        v = validateEmail(value, warnings, 'Email da Empresa');
      } else if (target.key === 'contactoRepresentante') {
        v = validateContacto(value, warnings);
      } else {
        v = value.trim();
      }
      empresa[target.key] = v;
    }
  }

  if (currentService && serviceBlockHasContent(currentService.fields)) {
    services.push(currentService);
  }

  if (recognized === 0) {
    errors.push('Nenhum campo reconhecido. Confirme que usa o modelo (Campo - valor # comentário).');
  }

  if (services.length > MAX_SERVICE_INSTANCES) {
    errors.push(
      `Demasiados serviços (${services.length}). Máximo ${MAX_SERVICE_INSTANCES} por importação.`
    );
  }

  // Deduplicate instance numbers per service type → renumber by appearance order
  const byServiceCount: Record<string, number> = {};
  const normalizedServices: ServiceInstanceImport[] = [];
  for (const svc of services) {
    byServiceCount[svc.service] = (byServiceCount[svc.service] || 0) + 1;
    normalizedServices.push({
      ...svc,
      instanceNumber: byServiceCount[svc.service],
    });
  }

  return {
    ok: errors.length === 0,
    data: {
      equipaInterna,
      empresa,
      services: normalizedServices,
    },
    errors,
    warnings,
  };
}

export type SessionIdentity = {
  nomeUsuario?: string;
  codigoComercial?: string;
  equipa?: string;
  emailUsuario?: string;
};

/**
 * Apply parsed import onto current proposal form (overwrite sections; strip files; keep identity).
 */
export function applyProposalNotesImport(
  parsed: PartialProposalImport,
  current: ProposalFormState,
  sessionIdentity: SessionIdentity
): ProposalFormState {
  const quantities = EMPTY_QUANTITIES();
  const servicos: ProposalFormState['servicos'] = [];
  const serviceDetails: Record<string, any> = {};

  for (const svc of parsed.services) {
    quantities[svc.service] = (quantities[svc.service] || 0) + 1;
    const instanceNumber = quantities[svc.service];
    const instanceId = `${svc.service}-${instanceNumber}`;
    servicos.push({
      service: svc.service,
      instanceNumber,
      instanceId,
    });

    const details: Record<string, any> = { ...svc.fields };
    // Never carry file blobs / metadata from import
    delete details.fatura;
    delete details.printTelhado;
    delete details.fatura_name;
    delete details.fatura_size;
    delete details.fatura_type;
    delete details.fatura_lead_file_id;
    delete details.printTelhado_name;
    delete details.printTelhado_size;
    delete details.printTelhado_type;
    delete details.printTelhado_lead_file_id;
    serviceDetails[instanceId] = details;
  }

  const tipoPedido = parsed.equipaInterna.tipoPedido || 'normal';

  return {
    ...current,
    equipaInterna: {
      ...current.equipaInterna,
      propostaMarcacao: parsed.equipaInterna.propostaMarcacao,
      assinaturaContrato: parsed.equipaInterna.assinaturaContrato,
      tipoPedido,
      // Session identity always wins
      nomeUsuario: sessionIdentity.nomeUsuario || current.equipaInterna?.nomeUsuario || '',
      codigoComercial:
        sessionIdentity.codigoComercial || current.equipaInterna?.codigoComercial || '',
      equipa: sessionIdentity.equipa || current.equipaInterna?.equipa || '',
      emailUsuario: sessionIdentity.emailUsuario || current.equipaInterna?.emailUsuario || '',
    },
    empresa: {
      ...emptyEmpresa(),
      ...parsed.empresa,
      // Keep moradaFornecimento slot on empresa object for legacy shape (not used in step 2 UI)
      moradaFornecimento: current.empresa?.moradaFornecimento || '',
    },
    servicos,
    serviceQuantities: quantities,
    serviceDetails,
  };
}

/** Download the template as modelo-solicitar-proposta.txt */
export function downloadProposalNotesTemplate(): void {
  const blob = new Blob([PROPOSAL_NOTES_TEMPLATE], {
    type: 'text/plain;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'modelo-solicitar-proposta.txt';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function copyProposalNotesTemplate(): Promise<void> {
  await navigator.clipboard.writeText(PROPOSAL_NOTES_TEMPLATE);
}
